import { useEffect, useState } from "react";
import { Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { boxQty } from "../lib/stock";
import { useAuth } from "../context/AuthContext";

const today = () => new Date().toISOString().slice(0, 10);
const canUndo = (l, user) => {
  if (l.undone_by_log_id || l.undo_of) return false;
  if (/^Auto-(deduct|receive)/.test(l.note || "")) return false;
  if (user?.role === "admin") return true;
  return l.user_id === user?.id && (l.created_at || "").slice(0, 10) === today();
};

// Admin: put a location's counts back to how they were at the end of a chosen day.
const RestoreDay = ({ onDone }) => {
  const [locs, setLocs] = useState([]);
  const [loc, setLoc] = useState("");
  const [date, setDate] = useState("");
  const [preview, setPreview] = useState(null);
  useEffect(() => { api.get("/locations").then((r) => setLocs(r.data)); }, []);
  const tz = new Date().getTimezoneOffset();
  const check = async () => {
    const r = await api.get(`/admin/inventory/as-of?location_id=${loc}&date=${date}&tz_offset=${tz}`);
    setPreview(r.data);
  };
  const restore = async () => {
    if (!window.confirm(`Restore ${preview.differences} item count(s) to end of ${date}? A backup is saved first.`)) return;
    const r = await api.post("/admin/inventory/restore", { location_id: loc, date, tz_offset: tz });
    toast.success(`Restored ${r.data.changed} item(s)`); setPreview(null); onDone();
  };
  const sel = "h-10 rounded-lg border border-input bg-card px-3 text-sm";
  return (
    <div className="luxury-card p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="restore-day">
      <div className="text-sm font-medium mr-2">Restore a previous day's counts</div>
      <select className={sel} value={loc} onChange={(e) => { setLoc(e.target.value); setPreview(null); }} data-testid="restore-location">
        <option value="">Location…</option>
        {locs.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
      </select>
      <input type="date" className={sel} value={date} max={today()} onChange={(e) => { setDate(e.target.value); setPreview(null); }} data-testid="restore-date" />
      <button onClick={check} disabled={!loc || !date} data-testid="restore-preview-btn" className="h-10 px-4 rounded-lg border border-border text-sm disabled:opacity-40">Preview</button>
      {preview && (
        <>
          <span className="text-xs text-muted-foreground" data-testid="restore-preview">{preview.differences} item(s) differ from end of {date}</span>
          <button onClick={restore} disabled={!preview.differences} data-testid="restore-apply-btn"
            className="h-10 px-4 rounded-lg bg-foreground text-background text-sm disabled:opacity-40">Restore</button>
        </>
      )}
    </div>
  );
};

export const InventoryLogTable = () => {
  const { user } = useAuth();
  const [logs, setLogs] = useState(null);
  const load = () => api.get("/inventory/logs?limit=500").then((r) => setLogs(r.data));
  useEffect(() => { load(); }, []);
  const undo = async (l) => {
    if (!window.confirm(`Undo this change to ${l.item_name}?`)) return;
    try {
      const r = await api.post(`/inventory/logs/${l.id}/undo`);
      toast.success(`Undone — ${l.item_name} now ${boxQty(r.data.new_stock, l.category)}`); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't undo"); }
  };
  return (
    <div>
      {user?.role === "admin" && <RestoreDay onDone={load} />}
      <div className="luxury-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="inventory-log-table">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              {["When", "Location", "Item", "Change", "New Stock", "By", "Note", ""].map((h, i) => (
                <th key={i} className="px-5 py-3 label-eyebrow font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(logs || []).map((l) => (
              <tr key={l.id} className={`border-t border-border ${l.undone_by_log_id ? "opacity-50" : ""}`} data-testid={`inventory-log-row-${l.id}`}>
                <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                <td className="px-5 py-3">{l.location_name || "—"}</td>
                <td className="px-5 py-3 font-medium">{l.item_name}</td>
                <td className={`px-5 py-3 tabular-nums ${l.change < 0 ? "text-destructive" : "text-[hsl(var(--accent-sage))]"}`}>
                  {l.change > 0 ? "+" : ""}{boxQty(l.change, l.category)}
                </td>
                <td className="px-5 py-3 tabular-nums">{boxQty(l.new_stock, l.category)}</td>
                <td className="px-5 py-3 text-muted-foreground">{l.user_name}</td>
                <td className="px-5 py-3 text-muted-foreground">{l.note}{l.undone_by_log_id && <span className="ml-1 text-xs">(undone by {l.undone_by})</span>}</td>
                <td className="px-3 py-3">
                  {canUndo(l, user) && (
                    <button onClick={() => undo(l)} data-testid={`undo-log-${l.id}`}
                      className="h-8 px-3 rounded-lg border border-border text-xs inline-flex items-center gap-1 hover:bg-secondary">
                      <Undo2 className="h-3.5 w-3.5" /> Undo
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {logs && logs.length === 0 && (
              <tr><td colSpan="8" className="px-5 py-16 text-center text-muted-foreground text-sm">No stock changes yet.</td></tr>
            )}
            {!logs && (
              <tr><td colSpan="8" className="px-5 py-16 text-center text-muted-foreground text-sm">Loading…</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
